import { GammaAPIService } from '../service/gamma-api.service';
import { BerWowSettings, MenuDisplayOption } from '../types/global';
import { defineStore } from 'pinia'
import { useRecommendations } from './recommendations';
import { useSelections } from './selections';
import { BerwowEvent, decodeJWT, emit } from '@/helpers';
import { useTour } from '@/composeables/tour';

interface SettingsState {
  settings: BerWowSettings | undefined,
  byBwid: boolean,
  apiToken: string | undefined,
  autoStartTour: boolean,
  tour: any,
  bwid: string | undefined,
  clientSessionId: string,
}

export const useSettings = defineStore('settings', {
  state: (): SettingsState => ({
    settings: undefined,
    byBwid: false,
    bwid: undefined,
    apiToken: undefined,
    autoStartTour: true,
    tour: undefined,
    clientSessionId: '',
  }),
  getters: {
    hasCostsAndSavings(): boolean {
      const i = this.settings?.recommendationsPageOptions?.menuDisplay?.indexOf(
        MenuDisplayOption.CostsAndSavings
      );
      return i != undefined && i != -1;
    }
  },
  actions: {
    // any amount of arguments, return a promise or not
    async setSettings(settings: BerWowSettings) {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get('bwid');
      this.byBwid = id != undefined;

      this.settings = { ...settings };
      await this.loadLoanOptions();
      await this.loadApiKey(this.settings);

      if (id) {
        this.bwid = id;
        await useRecommendations().loadRecommendationsById(id);
        useSelections().submitted = true;
      }

      emit(BerwowEvent.SettingsLoaded, settings);
    },

    setByBwId(val: boolean) {
      this.byBwid = val;
      if (!val) {
        this.bwid = undefined;
      }
    },

    setAutoStartTour(val: boolean) {
      this.autoStartTour = val;
    },

    async loadLoanOptions() {
      if (!this.settings) return;

      this.settings.loanOptions = await Promise.all(this.settings.loanOptions.map(opt => {
        return typeof opt === "string" ? Promise.resolve(GammaAPIService.getLoanOption(opt)) : Promise.resolve(opt)
      }));
    },

    async loadApiKey(settings: BerWowSettings) {
      this.apiToken = (await GammaAPIService.validateKey({
        ClientConfig: {
          orgKey: settings.orgKey,
          actionButtonText: settings.actionButtonText,
          autoaddressKey: settings.autoaddressKey,
          autoaddressProfile: settings.autoaddressProfile,
          loanOptions: settings.loanOptions ? settings.loanOptions : undefined,
          defaultLoanOption: settings.defaultLoanOption,
          targetDefaultEnergyRating: settings.targetDefaultEnergyRating,
        },
        Key: settings.orgKey,
        ClientVersion: "4.0.9",
        timestamp: Date.now()
      })).token;
      this.clientSessionId = decodeJWT(this.apiToken + '')['client-session-id'];
    },

    async reloadApiKey(): Promise<void> {
      if (this.settings)
        await this.loadApiKey(this.settings);
    },

    clearAutoaddress() {
      if (this.settings) {
        this.settings.autoaddressKey = ""
      }
    },

    initializeTour() {
      this.tour = useTour();
    }
  },
});

